/* ============================================================
   storage — modals.

   Three dialogs the file view needs: name something, choose a
   destination, confirm a deletion. Each traps focus, closes on
   Escape, and returns focus where it came from — a file manager
   is keyboard work more than it is mouse work.
   ============================================================ */

import React, { useEffect, useRef, useState } from 'react';
import { Icon, fmtBytes } from './lib';
import { api } from './api';

export function Modal({ title, children, onClose, footer, wide }) {
  const ref = useRef(null);
  const returnTo = useRef(typeof document !== 'undefined' ? document.activeElement : null);

  useEffect(() => {
    const node = ref.current;
    const restore = returnTo.current;      // captured now; the ref moves on
    node?.querySelector('input, button, [tabindex]')?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab' || !node) return;
      const focusable = node.querySelectorAll('button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      restore?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop st-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal st-modal ${wide ? 'st-modal--wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <div className="modal-head">
          <span className="label">{title}</span>
          <button type="button" className="iconbtn" onClick={onClose} aria-label="Close"><Icon name="close" size={14}/></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function NameModal({ title, label, initial = '', confirmLabel = 'Save', hint, onSubmit, onClose }) {
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e?.preventDefault();
    const name = value.trim();
    if (!name) { setErr('Give it a name'); return; }
    if (name.includes('/')) { setErr('Names cannot contain a slash'); return; }
    setBusy(true); setErr('');
    try { await onSubmit(name); onClose(); }
    catch (ex) { setErr(ex.message || 'That did not work'); setBusy(false); }
  };
  return (
    <Modal title={title} onClose={onClose} footer={
      <>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn--sm" onClick={submit} disabled={busy}>{busy ? 'Working…' : confirmLabel}</button>
      </>
    }>
      <form onSubmit={submit} className="field st-field">
        <label htmlFor="st-name">{label}</label>
        <input id="st-name" className="input" value={value} autoFocus
               onChange={(e) => { setValue(e.target.value); setErr(''); }}/>
        {hint && <span className="help">{hint}</span>}
        {err && <span className="err-msg">{err}</span>}
      </form>
    </Modal>
  );
}

/** `option` adds one checkbox ({label, initial}); when it is there, `body`,
 *  `confirmLabel` and `danger` may be functions of it, and onConfirm gets it. */
export function ConfirmModal({ title, body, confirmLabel = 'Delete', danger = true, onConfirm, onClose, option }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [opt, setOpt] = useState(!!option?.initial);
  const pick = (v) => (typeof v === 'function' ? v(opt) : v);
  const go = async () => {
    setBusy(true);
    try { await onConfirm(opt); onClose(); }
    catch (e) { setErr(e.message || 'That did not work'); setBusy(false); }
  };
  return (
    <Modal title={title} onClose={onClose} footer={
      <>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
        <button type="button" className={`btn btn--sm ${pick(danger) ? 'btn--danger' : ''}`} onClick={go} disabled={busy}>
          {busy ? 'Working…' : pick(confirmLabel)}
        </button>
      </>
    }>
      <div className="st-confirm">{pick(body)}</div>
      {option && (
        <label className="st-confirm-opt">
          <input type="checkbox" checked={opt} onChange={(e) => setOpt(e.target.checked)}/>
          <span>{option.label}</span>
        </label>
      )}
      {err && <div className="alert alert--err st-alert">{err}</div>}
    </Modal>
  );
}

/** Destination picker: walks folders on either drive, and refuses to offer a
 *  folder you are moving into itself. Also the "where should this go" picker
 *  for anything else that lands in a folder (a saved trim): the words change,
 *  the walking does not. */
export function MoveModal({
  token, drives, fromDrive, fromPath = '/', blocked = [], onMove, onClose,
  title = 'Move to', confirmLabel = 'Move here', busyLabel = 'Moving…',
  crossDriveNote = 'Moving to another drive copies the bytes across, so it takes as long as the files are big.',
}) {
  const [drive, setDrive] = useState(fromDrive);
  const [path, setPath] = useState(fromPath);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true); setErr('');
    api.folders(token, drive, path)
      .then((rows) => { if (live) setFolders(rows.filter((f) => !f.locked)); })
      .catch((e) => { if (live) setErr(e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [token, drive, path]);

  const blockedHere = (f) => blocked.some((b) => b.drive === drive && (f.path === b.path || f.path.startsWith(b.path + '/')));
  const segs = path === '/' ? [] : path.slice(1).split('/');
  const driveRow = drives.find((d) => d.id === drive);

  return (
    <Modal title={title} onClose={onClose} wide footer={
      <>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn--sm" disabled={busy || !driveRow?.online} onClick={async () => {
          setBusy(true);
          try { await onMove(path, drive); onClose(); }
          catch (e) { setErr(e.message); setBusy(false); }
        }}>
          {busy ? busyLabel : confirmLabel}
        </button>
      </>
    }>
      <div className="st-move-drives">
        {drives.map((d) => (
          <button key={d.id} type="button"
                  className={`st-chip ${d.id === drive ? 'on' : ''}`}
                  aria-pressed={d.id === drive}
                  disabled={!d.online}
                  onClick={() => { setDrive(d.id); setPath('/'); }}>
            {d.label}{!d.online ? ' (offline)' : ''}
          </button>
        ))}
      </div>

      <div className="st-move-crumbs">
        <button type="button" className="st-crumb" onClick={() => setPath('/')}>{driveRow?.label || drive}</button>
        {segs.map((s, i) => (
          <React.Fragment key={s + i}>
            <span className="st-crumb-sep">/</span>
            <button type="button" className="st-crumb" onClick={() => setPath('/' + segs.slice(0, i + 1).join('/'))}>{s}</button>
          </React.Fragment>
        ))}
      </div>

      {drive !== fromDrive && crossDriveNote && (
        <div className="alert alert--info st-alert">{crossDriveNote}</div>
      )}
      {err && <div className="alert alert--err st-alert">{err}</div>}

      <div className="st-move-list">
        {loading && <div className="skeleton st-skel"/>}
        {!loading && folders.length === 0 && <div className="empty st-empty-sm">No folders here — you can still move into this one.</div>}
        {!loading && folders.map((f) => (
          <button key={f.id} type="button" className="listrow st-move-row"
                  disabled={blockedHere(f)}
                  onClick={() => setPath(f.path)}>
            <Icon name={f.secure ? 'folderLock' : 'folder'} size={16}/>
            <span className="st-move-name">{f.name}</span>
            {blockedHere(f) && <span className="meta">can't move into itself</span>}
            <Icon name="chevron" size={14}/>
          </button>
        ))}
      </div>
    </Modal>
  );
}

export function FolderInfo({ folder, onClose }) {
  return (
    <Modal title="Folder" onClose={onClose}>
      <dl className="st-facts">
        <div><dt>Name</dt><dd>{folder.name}</dd></div>
        <div><dt>Made by</dt><dd>{folder.owner_name || 'unknown'}</dd></div>
        <div><dt>Access</dt><dd>{folder.secure ? 'Secure — owner and admins only' : 'Everyone can read'}</dd></div>
        <div><dt>Path</dt><dd>{folder.path}</dd></div>
      </dl>
    </Modal>
  );
}

export { fmtBytes };
